import {
    AccountingCategoryWithRoutedItemType,
    NewMultipleRoutingRuleAddParameters,
    NewMultipleRoutingRuleUpdateParameters,
    NewRoutedItemTypesParameters,
    NewRoutingRuleAssignmentAddParameters,
    NewRoutingRuleAssignmentUpdateParameters,
    NewRoutingRuleResult,
    RoutingRuleAssignmentTargetType,
    RoutingRuleBillAggregationType,
    RoutingRulePrepayment,
    RoutingRuleTriggerType,
} from '@mews-commander/data-access';
import { DateTimeLocalizationInfo, DurationService, TimeService } from '@mews/framework';

import {
    AdditionalServiceFormValues,
    AdditionalServiceOptionsNames,
    AdditionalServicesFormValues,
    CompaniesFormValues,
    CustomItemsFormValues,
    CustomItemsOptionsNames,
    OptionsNames,
    ProductsFormValues,
    ProductsOptionsNames,
    RatesOptionsNames,
    RoutingRuleFormValues,
    ServiceFormValues,
    ServicesFormValues,
    TargetBillType,
} from '../schemas';
import { areArraysEqual } from '../components/form/steps/itemsAndConditions/items/utils';

const getTargetType = (targetType: TargetBillType, includeCompanyDetails: string[]) => {
    if (targetType === TargetBillType.CompanyBill) {
        return RoutingRuleAssignmentTargetType.CompanyAsOwner;
    }
    return includeCompanyDetails.length === 0
        ? RoutingRuleAssignmentTargetType.NoCompany
        : RoutingRuleAssignmentTargetType.CompanyAsDetails;
};

const getAggregationType = (targetType: TargetBillType, aggregationType?: RoutingRuleBillAggregationType) =>
    targetType === TargetBillType.CompanyBill && aggregationType
        ? aggregationType
        : RoutingRuleBillAggregationType.AggregateByCustomer;

export const getRoutedItemTypesForBookableService = (
    serviceFormValues: ServiceFormValues
): NewRoutedItemTypesParameters => {
    const { serviceOptions, serviceProducts } = serviceFormValues;
    const isProductsOptionSelected = serviceOptions.includes(OptionsNames.Products);
    return {
        cityTax: serviceOptions.includes(OptionsNames.CityTaxes),
        spaceOrder: serviceOptions.includes(OptionsNames.TimeUnit),
        allProducts: isProductsOptionSelected && !!serviceProducts && serviceProducts.type === ProductsOptionsNames.All,
        deposits: serviceOptions.includes(OptionsNames.Deposits),
        additionalExpenses: serviceOptions.includes(OptionsNames.AdditionalExpenses),
        allCustomItems: false,
    };
};

export const getRoutedItemTypesForAdditionalService = (
    serviceFormValues: AdditionalServiceFormValues
): NewRoutedItemTypesParameters => {
    const { serviceOptions, serviceProducts, customItems } = serviceFormValues;
    const isProductsOptionSelected = serviceOptions.includes(AdditionalServiceOptionsNames.Products);
    const isCustomItemsOptionSelected = serviceOptions.includes(AdditionalServiceOptionsNames.CustomItems);
    return {
        cityTax: false,
        spaceOrder: false,
        allProducts: isProductsOptionSelected && !!serviceProducts && serviceProducts.type === ProductsOptionsNames.All,
        deposits: false,
        additionalExpenses: false,
        allCustomItems:
            isCustomItemsOptionSelected && !!customItems && customItems.type === CustomItemsOptionsNames.All,
    };
};

const getCompanies = (companies: CompaniesFormValues) =>
    Object.entries(companies)
        .filter(([_, { linkAsCompany, linkAsTravelAgency }]) => linkAsCompany || linkAsTravelAgency)
        .map(([key, company]) => ({
            companyId: key,
            companyName: company.name,
            companyRelations: {
                partnerCompany: company.linkAsCompany,
                travelAgency: !!company.linkAsTravelAgency,
            },
        }));

const getProcessingStartOffset = (
    triggerDay: number,
    triggerTime: string,
    localizationInfo: DateTimeLocalizationInfo
) => {
    const { value } = TimeService.fromShortString(triggerTime, localizationInfo);
    const parsedTime = TimeService.parse(value);
    const duration = DurationService.fromObject({
        days: triggerDay - 1,
        hours: TimeService.getHours(parsedTime),
        minutes: TimeService.getMinutes(parsedTime),
    });
    return DurationService.toString(duration);
};

const getProductsForAssignment = (serviceProducts: ProductsFormValues, isProductsOptionSelected: boolean) => ({
    productIds:
        isProductsOptionSelected &&
        serviceProducts &&
        serviceProducts.type === ProductsOptionsNames.SelectedProducts &&
        serviceProducts.selectedProducts?.length
            ? serviceProducts.selectedProducts
            : null,
    productCategoryIds:
        isProductsOptionSelected &&
        serviceProducts &&
        serviceProducts.type === ProductsOptionsNames.SelectedProductCategories &&
        serviceProducts.selectedProductCategories?.length
            ? serviceProducts.selectedProductCategories
            : null,
});

const getAccountingCategoriesForAssignment = (
    serviceProducts: ProductsFormValues,
    customItems: CustomItemsFormValues,
    isProductsOptionSelected: boolean,
    isCustomItemsOptionSelected: boolean
): AccountingCategoryWithRoutedItemType[] | undefined => {
    const accountingCategoriesForProducts =
        isProductsOptionSelected &&
        serviceProducts &&
        serviceProducts.type === ProductsOptionsNames.SelectedAccountingCategories &&
        serviceProducts?.selectedAccountingCategories?.length
            ? serviceProducts.selectedAccountingCategories
            : null;
    const accountingCategoriesForCustomItems =
        isCustomItemsOptionSelected &&
        customItems &&
        customItems.type === CustomItemsOptionsNames.SelectedAccountingCategories &&
        customItems?.selectedAccountingCategories?.length
            ? customItems.selectedAccountingCategories
            : null;

    const accountingCategories = accountingCategoriesForProducts || accountingCategoriesForCustomItems;
    const isProductItemType = !!accountingCategoriesForProducts?.length;
    const isCustomItemType = !!accountingCategoriesForCustomItems?.length;

    return accountingCategories?.map((accountingCategoryId) => ({
        accountingCategoryId,
        itemTypes: {
            products: isProductItemType,
            customItems: isCustomItemType,
        },
    }));
};

const getAssignments = ({
    services,
    ratesEnabled,
    additionalServices,
}: {
    services: ServicesFormValues;
    ratesEnabled: boolean;
    additionalServices?: AdditionalServicesFormValues;
}): NewRoutingRuleAssignmentAddParameters[] => {
    const bookableServiceAssignments = Object.entries(services)
        .filter(([_, value]) => value.serviceEnabled)
        .map(([key, value]) => {
            const { serviceOptions, serviceProducts, serviceRates } = value;
            const isProductsOptionSelected = serviceOptions.includes(OptionsNames.Products);
            return {
                serviceId: key,
                rateIds:
                    ratesEnabled &&
                    serviceRates &&
                    serviceRates.type === RatesOptionsNames.Selected &&
                    serviceRates.selectedRates?.length
                        ? serviceRates.selectedRates
                        : null,
                routedItemTypes: getRoutedItemTypesForBookableService(value),
                ...getProductsForAssignment(serviceProducts, isProductsOptionSelected),
            };
        });
    const additionalServiceAssignments = additionalServices
        ? Object.entries(additionalServices)
              .filter(([_, value]) => value.serviceEnabled)
              .map(([key, value]) => {
                  const { serviceOptions, serviceProducts, customItems } = value;
                  const isProductsOptionSelected = serviceOptions.includes(AdditionalServiceOptionsNames.Products);
                  const isCustomItemsOptionSelected = serviceOptions.includes(
                      AdditionalServiceOptionsNames.CustomItems
                  );
                  return {
                      serviceId: key,
                      rateIds: null,
                      routedItemTypes: getRoutedItemTypesForAdditionalService(value),
                      accountingCategories: getAccountingCategoriesForAssignment(
                          serviceProducts,
                          customItems,
                          isProductsOptionSelected,
                          isCustomItemsOptionSelected
                      ),
                      ...getProductsForAssignment(serviceProducts, isProductsOptionSelected),
                  };
              })
        : [];

    return [...bookableServiceAssignments, ...additionalServiceAssignments];
};

export const prepareRoutingRulesValues = (
    values: RoutingRuleFormValues,
    localizationInfo: DateTimeLocalizationInfo,
    recurringRoutingRulesEnabled: boolean
): NewMultipleRoutingRuleAddParameters => {
    const { timing, itemsAndConditions, targetBill } = values;
    const { description, name, triggerTiming, triggerDay, triggerTime, consumptionDate } = timing;
    const { services, additionalServices, companies, prepaidReservationsEnabled, ratesEnabled, companiesEnabled } =
        itemsAndConditions;
    const { aggregationType, targetType, includeCompanyDetails } = targetBill;
    return {
        routingRules: [
            {
                companies: companiesEnabled && companies ? getCompanies(companies) : [],
                name,
                description,
                prepayment: prepaidReservationsEnabled ? RoutingRulePrepayment.Prepaid : RoutingRulePrepayment.All,
                assignmentTargetType: getTargetType(targetType, includeCompanyDetails),
                triggerType: triggerTiming,
                billAggregationType: getAggregationType(targetType, aggregationType),
                orderItemConsumptionPeriod:
                    recurringRoutingRulesEnabled && triggerTiming === RoutingRuleTriggerType.Recurring
                        ? consumptionDate
                        : null,
                processingStartOffset:
                    recurringRoutingRulesEnabled &&
                    triggerTiming === RoutingRuleTriggerType.Recurring &&
                    triggerDay != null &&
                    triggerTime != null
                        ? getProcessingStartOffset(triggerDay, triggerTime, localizationInfo)
                        : null,
                assignments: getAssignments({ services, additionalServices, ratesEnabled }),
            },
        ],
    };
};

export const getRoutingRuleFromFormValues = (
    formValues: RoutingRuleFormValues,
    recurringRoutingRulesEnabled: boolean,
    localizationInfo: DateTimeLocalizationInfo
) => {
    const { timing, itemsAndConditions, targetBill } = formValues;
    const { description, name, triggerTiming, triggerDay, triggerTime } = timing;
    const { companies, companiesEnabled, prepaidReservationsEnabled } = itemsAndConditions;
    const { aggregationType, targetType, includeCompanyDetails } = targetBill;

    return {
        name,
        description,
        companiesWithRelations: companiesEnabled && companies ? getCompanies(companies) : [],
        prepayment: prepaidReservationsEnabled ? RoutingRulePrepayment.Prepaid : RoutingRulePrepayment.All,
        assignmentTargetType: getTargetType(targetType, includeCompanyDetails),
        triggerType: triggerTiming,
        billAggregationType: getAggregationType(targetType, aggregationType),
        processingStartOffset:
            recurringRoutingRulesEnabled &&
            triggerTiming === RoutingRuleTriggerType.Recurring &&
            triggerDay != null &&
            triggerTime != null
                ? getProcessingStartOffset(triggerDay, triggerTime, localizationInfo)
                : null,
    };
};

const areCompaniesChanged = (
    initialCompanies: ReturnType<typeof getCompanies>,
    currentCompanies: ReturnType<typeof getCompanies>
) => {
    if (initialCompanies.length !== currentCompanies.length) return true;

    return initialCompanies.some((company, index) => {
        const currentCompany = currentCompanies[index];
        return (
            company.companyId !== currentCompany.companyId ||
            company.companyRelations.partnerCompany !== currentCompany.companyRelations.partnerCompany ||
            company.companyRelations.travelAgency !== currentCompany.companyRelations.travelAgency
        );
    });
};

const areAccountingCategoriesChanged = (
    initialAccountingCategories?: AccountingCategoryWithRoutedItemType[] | null,
    currentAccountingCategories?: AccountingCategoryWithRoutedItemType[] | null
) => {
    if (initialAccountingCategories == null || currentAccountingCategories == null) {
        return initialAccountingCategories !== currentAccountingCategories;
    }
    if (initialAccountingCategories.length !== currentAccountingCategories.length) return true;

    return initialAccountingCategories.some((accountingCategory, index) => {
        const currentAccountingCategory = currentAccountingCategories[index];
        return (
            accountingCategory.accountingCategoryId !== currentAccountingCategory.accountingCategoryId ||
            accountingCategory.itemTypes.products !== currentAccountingCategory.itemTypes.products ||
            accountingCategory.itemTypes.customItems !== currentAccountingCategory.itemTypes.customItems
        );
    });
};

const getUpdatedAssignments = (
    initialAssignments: NewRoutingRuleAssignmentAddParameters[],
    currentAssignments: NewRoutingRuleAssignmentAddParameters[],
    routingRuleResult?: NewRoutingRuleResult
): NewRoutingRuleAssignmentUpdateParameters[] =>
    currentAssignments.reduce((acc: NewRoutingRuleAssignmentUpdateParameters[], currentAssignment) => {
        const initialAssignment = initialAssignments.find(
            (service) => service.serviceId === currentAssignment.serviceId
        );

        if (!initialAssignment) return acc;

        const routedItemTypesChanged = !Object.entries(initialAssignment.routedItemTypes).every(
            ([key, value]) => value === currentAssignment.routedItemTypes[key as keyof NewRoutedItemTypesParameters]
        );

        const productIdsChanged = !areArraysEqual(initialAssignment.productIds, currentAssignment.productIds);
        const productCategoryIdsChanged = !areArraysEqual(
            initialAssignment.productCategoryIds,
            currentAssignment.productCategoryIds
        );
        const rateIdsChanged = !areArraysEqual(initialAssignment.rateIds, currentAssignment.rateIds);
        const accountingCategoriesChanged = areAccountingCategoriesChanged(
            initialAssignment.accountingCategories,
            currentAssignment.accountingCategories
        );

        if (
            routedItemTypesChanged ||
            productIdsChanged ||
            productCategoryIdsChanged ||
            rateIdsChanged ||
            accountingCategoriesChanged
        ) {
            acc.push({
                routingRuleAssignmentId:
                    routingRuleResult?.routingRuleAssignments?.find(
                        (assignment) => assignment.serviceId === currentAssignment.serviceId
                    )?.id || '',
                routedItemTypes: routedItemTypesChanged ? { value: currentAssignment.routedItemTypes } : undefined,
                productIds: productIdsChanged ? { value: currentAssignment.productIds } : undefined,
                productCategoryIds: productCategoryIdsChanged
                    ? { value: currentAssignment.productCategoryIds }
                    : undefined,
                rateIds: rateIdsChanged ? { value: currentAssignment.rateIds } : undefined,
                accountingCategories: accountingCategoriesChanged
                    ? { value: currentAssignment.accountingCategories }
                    : undefined,
            });
        }

        return acc;
    }, []);

const getNewAssignments = (
    initialAssignments: NewRoutingRuleAssignmentAddParameters[],
    currentAssignments: NewRoutingRuleAssignmentAddParameters[]
): NewRoutingRuleAssignmentAddParameters[] =>
    currentAssignments.filter(
        (currentAssignment) =>
            !initialAssignments.some((initialAssignment) => initialAssignment.serviceId === currentAssignment.serviceId)
    );

const getRemovedAssignmentIds = (
    initialAssignments: NewRoutingRuleAssignmentAddParameters[],
    currentAssignments: NewRoutingRuleAssignmentAddParameters[],
    routingRuleResult?: NewRoutingRuleResult
): string[] =>
    initialAssignments
        .filter(
            (initialAssignment) =>
                !currentAssignments.some(
                    (currentAssignment) => currentAssignment.serviceId === initialAssignment.serviceId
                )
        )
        .map(
            (initialAssignment) =>
                routingRuleResult?.routingRuleAssignments?.find(
                    (assignment) => assignment.serviceId === initialAssignment.serviceId
                )?.id
        )
        .filter((id) => id != null);

export const prepareRoutingRulesUpdateValues = ({
    values,
    recurringRoutingRulesEnabled,
    routingRuleId,
    initialValues,
    routingRuleResult,
    localizationInfo,
}: {
    values: RoutingRuleFormValues;
    recurringRoutingRulesEnabled: boolean;
    routingRuleId: string;
    initialValues: RoutingRuleFormValues;
    routingRuleResult?: NewRoutingRuleResult;
    localizationInfo: DateTimeLocalizationInfo;
}): NewMultipleRoutingRuleUpdateParameters => {
    const { timing, itemsAndConditions, targetBill } = values;
    const { description, name, triggerTiming, triggerDay, triggerTime, consumptionDate } = timing;
    const { services, additionalServices, companies, prepaidReservationsEnabled, ratesEnabled, companiesEnabled } =
        itemsAndConditions;
    const { aggregationType, targetType, includeCompanyDetails } = targetBill;

    const initialCompanies =
        initialValues.itemsAndConditions.companiesEnabled && initialValues.itemsAndConditions.companies
            ? getCompanies(initialValues.itemsAndConditions.companies)
            : [];
    const currentCompanies = companiesEnabled && companies ? getCompanies(companies) : [];

    const initialAssignments = getAssignments({
        services: initialValues.itemsAndConditions.services,
        additionalServices: initialValues.itemsAndConditions.additionalServices,
        ratesEnabled: initialValues.itemsAndConditions.ratesEnabled,
    });
    const currentAssignments = getAssignments({ services, additionalServices, ratesEnabled });

    const assignmentIdsToRemove = getRemovedAssignmentIds(initialAssignments, currentAssignments, routingRuleResult);
    const assignmentsToAdd = getNewAssignments(initialAssignments, currentAssignments);
    const assignmentsToUpdate = getUpdatedAssignments(initialAssignments, currentAssignments, routingRuleResult);
    const initialAssignmentTargetType = getTargetType(
        initialValues.targetBill.targetType,
        initialValues.targetBill.includeCompanyDetails
    );
    const assignmentTargetType = getTargetType(targetType, includeCompanyDetails);

    return {
        routingRuleUpdates: [
            {
                routingRuleId,
                name: initialValues.timing.name !== name ? { value: name } : undefined,
                description: initialValues.timing.description !== description ? { value: description } : undefined,
                // @ts-expect-error $TSFixMeError: Type { value:[value] } is expected on backend but generated type is not correct
                billAggregationType:
                    initialValues.targetBill.aggregationType !== aggregationType ||
                    initialAssignmentTargetType !== assignmentTargetType
                        ? { value: getAggregationType(targetType, aggregationType) }
                        : undefined,
                // @ts-expect-error $TSFixMeError: Type { value:[value] } is expected on backend but generated type is not correct
                assignmentTargetType:
                    initialAssignmentTargetType !== assignmentTargetType ? { value: assignmentTargetType } : undefined,
                // @ts-expect-error $TSFixMeError: Type { value:[value] } is expected on backend but generated type is not correct
                prepayment:
                    initialValues.itemsAndConditions.prepaidReservationsEnabled !== prepaidReservationsEnabled
                        ? {
                              value: prepaidReservationsEnabled
                                  ? RoutingRulePrepayment.Prepaid
                                  : RoutingRulePrepayment.All,
                          }
                        : undefined,
                triggerType:
                    initialValues.timing.triggerTiming !== triggerTiming ? { value: triggerTiming } : undefined,
                processingStartOffset:
                    recurringRoutingRulesEnabled &&
                    triggerTiming === RoutingRuleTriggerType.Recurring &&
                    triggerDay != null &&
                    triggerTime != null &&
                    (initialValues.timing.triggerTiming === RoutingRuleTriggerType.Continuous ||
                        initialValues.timing.triggerDay !== triggerDay ||
                        initialValues.timing.triggerTime !== triggerTime)
                        ? { value: getProcessingStartOffset(triggerDay, triggerTime, localizationInfo) }
                        : undefined,
                // @ts-expect-error Type 'RoutingRuleOrderItemConsumptionPeriodType.MonthBefore' is not assignable to type 'RoutingRuleOrderItemConsumptionPeriodTypeEnum | undefined'.
                orderItemConsumptionPeriod:
                    recurringRoutingRulesEnabled &&
                    triggerTiming === RoutingRuleTriggerType.Recurring &&
                    consumptionDate != null &&
                    (initialValues.timing.triggerTiming === RoutingRuleTriggerType.Continuous ||
                        initialValues.timing.consumptionDate !== consumptionDate)
                        ? { value: consumptionDate }
                        : undefined,
                companies: areCompaniesChanged(initialCompanies, currentCompanies)
                    ? { value: currentCompanies }
                    : undefined,
                assignmentIdsToRemove: assignmentIdsToRemove.length > 0 ? assignmentIdsToRemove : undefined,
                assignmentsToAdd: assignmentsToAdd.length > 0 ? assignmentsToAdd : undefined,
                assignmentsToUpdate: assignmentsToUpdate.length > 0 ? assignmentsToUpdate : undefined,
            },
        ],
    };
};
